import { SyntheticEvent, useCallback, useEffect, useId, useRef, useState } from "react";

import { ConfirmationProof, TwoFactorMethod, sendStepUpCode } from "@/api/twoFactor";
import { Button, Modal } from "@/components/ui";
import { Disclosure } from "@/components/ui/Disclosure";
import { headingVariants } from "@/components/ui/Heading";
import { FRONT_DOOR_INPUT, Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Notice } from "@/components/ui/Notice";
import { TextLink } from "@/components/ui/TextLink";
import { SUPPORT_EMAIL } from "@/config/support";
import { useAuthContext } from "@/context/auth";

import { describeError } from "./errorCopy";
import { supportResetHref } from "./supportReset";
import { useResendBudget } from "./useResendBudget";

type Mode = "code" | "password";

/**
 * Every state-changing action on the security page confirms here, with a code from the active
 * second factor - or, for an account that has one, with the password. The default is the code: it
 * is what the person has in hand, and the only proof an account that signs in through SSO can
 * offer. Backup codes are refused by the server on purpose (a finite login budget must not turn
 * into permanent access), and the helper text says why before the mistake is made rather than after.
 * Someone with no code at all gets "Can't get a code?", which points at support: only a superadmin
 * can reset the second factor.
 *
 * For an email account the dialog sends a code the moment it opens, the way the enrollment wizard
 * does; closing it does not invalidate that code - the server's ten-minute TTL does - so reopening
 * simply sends another, and the server's send budget is what bounds an abandoned dialog.
 */
export const ConfirmIdentityDialog = (props: {
  isOpen: boolean;
  title: string;
  consequence: string;
  confirmLabel: string;
  destructive?: boolean;
  error?: string;
  method: TwoFactorMethod;
  passwordAvailable: boolean;
  onConfirm: (proof: ConfirmationProof) => void;
  onCancel: () => void;
}) => {
  const {
    isOpen,
    title,
    consequence,
    confirmLabel,
    destructive,
    error,
    method,
    passwordAvailable,
    onConfirm,
    onCancel,
  } = props;
  const [mode, setMode] = useState<Mode>("code");
  const [value, setValue] = useState("");
  const [emailHint, setEmailHint] = useState<string | null>(null);
  const [sendError, setSendError] = useState("");
  const budget = useResendBudget();
  const { user } = useAuthContext();
  const { reset, recordSend, startCooldown, canResend, cooldownLeft } = budget;
  const inputId = useId();
  const errorId = useId();
  const hintId = useId();

  // `send` reads the method through a ref so that a method change while the dialog is open does
  // not give it a new identity: the reset effect below would re-fire, clear the typed value and
  // send another email code.
  const methodRef = useRef(method);
  useEffect(() => {
    methodRef.current = method;
  }, [method]);

  const send = useCallback(
    (isResend: boolean) => {
      setSendError("");
      if (isResend) {
        startCooldown();
      }
      sendStepUpCode()
        .then((sent) => {
          setEmailHint(sent.emailHint);
          recordSend();
        })
        .catch((e) => setSendError(describeError(e, methodRef.current)));
    },
    [startCooldown, recordSend],
  );

  // Opening or closing starts from a clean slate - no stale value, mode, hint or budget from an
  // earlier interaction - and an email account gets its code sent on open.
  useEffect(() => {
    setMode("code");
    setValue("");
    setEmailHint(null);
    setSendError("");
    reset();
    if (isOpen && methodRef.current === "EMAIL") {
      send(false);
    }
  }, [isOpen, reset, send]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setValue("");
  };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    // Belt-and-braces alongside the input's `required`: an empty value must never reach the
    // server as `{"code":""}` - that fails `@AssertTrue` and comes back as a code the frontend
    // does not know how to explain.
    if (!value.trim()) {
      return;
    }
    onConfirm(mode === "code" ? { code: value } : { password: value });
    // Cleared regardless of the outcome, so a repeated error never leaves a rejected value in place.
    setValue("");
  };

  const codeLabel = method === "EMAIL" ? "Code we sent you" : "Code from your authenticator app";
  const shownError = error || sendError;

  return (
    <Modal isOpen={isOpen} label={title} onRequestClose={onCancel}>
      <form onSubmit={submit} className="space-y-4">
        <h2 className={headingVariants({ variant: "subtitle" })}>{title}</h2>
        {destructive ? (
          <Notice tone="warning">
            <p className="text-foreground">{consequence}</p>
          </Notice>
        ) : (
          <p className="text-sm text-foreground">{consequence}</p>
        )}

        {mode === "code" && method === "EMAIL" && (
          <>
            <p role="status" className="text-sm">
              {emailHint
                ? `We sent a code to ${emailHint}.`
                : sendError
                  ? "We couldn't send you a code. Try again below."
                  : "Sending you a code…"}
            </p>
            {canResend ? (
              <button
                type="button"
                className="inline-flex min-h-11 min-w-11 items-center text-sm text-muted-foreground underline hover:text-foreground disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                disabled={cooldownLeft > 0}
                onClick={() => send(true)}
              >
                {cooldownLeft > 0 ? `Send a new code (${cooldownLeft}s)` : "Send a new code"}
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">
                No more codes can be sent right now. Wait for the last one to arrive, or come back
                in a few minutes.
              </p>
            )}
          </>
        )}

        <div className="space-y-2">
          <Label htmlFor={inputId} className="text-sm font-semibold">
            {mode === "code" ? codeLabel : "Password"}
          </Label>
          <Input
            id={inputId}
            className={FRONT_DOOR_INPUT}
            type={mode === "code" ? "text" : "password"}
            autoComplete={mode === "code" ? "one-time-code" : "current-password"}
            inputMode={mode === "code" ? "numeric" : undefined}
            autoFocus
            required
            // Only a rejected proof marks the field invalid; a failed send is not the field's fault
            aria-invalid={error ? true : undefined}
            aria-describedby={
              [shownError ? errorId : "", mode === "code" ? hintId : ""]
                .filter(Boolean)
                .join(" ") || undefined
            }
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          {shownError && (
            <p id={errorId} role="alert" className="text-sm font-semibold text-destructive-fg">
              {shownError}
            </p>
          )}
          {mode === "code" && (
            <p id={hintId} className="text-xs text-muted-foreground">
              Backup codes can't be used here: they sign you in, but can't change security settings.
              {method === "TOTP" &&
                " If you just used this code to sign in, wait for the next one."}
            </p>
          )}
        </div>

        {passwordAvailable && (
          <button
            type="button"
            className="inline-flex min-h-11 items-center text-sm text-muted-foreground underline hover:text-primary-fg"
            onClick={() => switchMode(mode === "code" ? "password" : "code")}
          >
            {mode === "code" ? "Use your password instead" : "Use a code instead"}
          </button>
        )}

        {mode === "code" && (
          <Disclosure summary="Can't get a code?">
            <p className="text-sm text-muted-foreground">
              Support can reset two-step verification for you. Email{" "}
              <TextLink href={supportResetHref(user?.username)}>{SUPPORT_EMAIL}</TextLink>.
            </p>
          </Disclosure>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" size="xl" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="xl" variant={destructive ? "destructive" : "brand"}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
